(() => {
  'use strict';

  const STATE_KEY = 'fmCollectionRestore';
  const MAX_AGE_MS = 2 * 60 * 60 * 1000;

  const parsePage = (value) => {
    try {
      const page = Number(
        new URL(value, window.location.href).searchParams.get('page') || 1
      );
      return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
    } catch {
      return 1;
    }
  };

  const collectionKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const entries = [...url.searchParams.entries()].sort(
      ([keyA, valueA], [keyB, valueB]) =>
        keyA === keyB
          ? valueA.localeCompare(valueB)
          : keyA.localeCompare(keyB)
    );

    url.search = '';
    entries.forEach(([key, value]) => url.searchParams.append(key, value));

    return `${url.pathname}${url.search}`;
  };

  const isFreshState = (state, key) =>
    state &&
    state.key === key &&
    Number.isFinite(Number(state.pagesLoaded)) &&
    Number(state.pagesLoaded) >= 1 &&
    Number.isFinite(Number(state.scrollY)) &&
    Number(state.savedAt) > Date.now() - MAX_AGE_MS;

  const readSavedState = (key) => {
    const state = history.state?.[STATE_KEY];
    return isFreshState(state, key) ? state : null;
  };

  const productPath = (productLink) => {
    try {
      return new URL(productLink.href, window.location.href).pathname;
    } catch {
      return '';
    }
  };

  const productCard = (grid, productLink) => {
    let card = productLink;
    while (card && card.parentElement !== grid) {
      card = card.parentElement;
    }
    return card && card.parentElement === grid ? card : productLink;
  };

  const rememberState = (section, grid, productLink) => {
    const card = productCard(grid, productLink);
    const rect = card.getBoundingClientRect();
    const key = collectionKey();

    const state = {
      key,
      pagesLoaded: Math.max(
        1,
        Number(section.dataset.fmLastLoadedPage || 1)
      ),
      scrollY: Math.max(0, Math.round(window.scrollY)),
      productPath: productPath(productLink),
      productViewportTop: Number.isFinite(rect.top)
        ? Math.round(rect.top)
        : null,
      savedAt: Date.now()
    };

    try {
      history.replaceState(
        { ...(history.state || {}), [STATE_KEY]: state },
        document.title
      );
    } catch {}
  };

  const syncViewMore = (section, nextRoot) => {
    const currentButton = section.querySelector('[data-fm-view-more]');
    const currentStatus = section.querySelector('[data-fm-view-more-status]');
    const nextButton = nextRoot.querySelector('[data-fm-view-more]');
    const nextStatus = nextRoot.querySelector('[data-fm-view-more-status]');

    if (nextButton && currentButton) {
      currentButton.href = nextButton.href;
      currentButton.textContent =
        (nextButton.textContent || 'View More').trim() || 'View More';
      currentButton.classList.remove('is-loading');
      currentButton.removeAttribute('aria-busy');

      if (currentStatus && nextStatus) {
        currentStatus.textContent = (nextStatus.textContent || '').trim();
      }
      return;
    }

    if (!nextButton) {
      section.querySelector('[data-fm-view-more-wrap]')?.remove();
    }
  };

  const fetchCollectionPage = async (section, page) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Collection restore failed: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = doc.getElementById(section.id);
    const nextGrid = nextRoot?.querySelector('[data-fm-product-grid]');

    if (!nextRoot || !nextGrid) {
      throw new Error('Collection restore markup not found');
    }

    return { nextRoot, nextGrid };
  };

  const nextFrame = () =>
    new Promise((resolve) => requestAnimationFrame(resolve));

  const revealCollection = () => {
    if (window.__fmCollectionRestoreTimer) {
      clearTimeout(window.__fmCollectionRestoreTimer);
      window.__fmCollectionRestoreTimer = null;
    }
    document.documentElement.classList.remove('fm-collection-restoring');
  };

  const findSavedProduct = (grid, path) => {
    if (!path) return null;

    const links = grid.querySelectorAll('a[href*="/products/"]');
    for (const link of links) {
      if (productPath(link) === path) return link;
    }
    return null;
  };

  const exactScrollTarget = (grid, state) => {
    const savedLink = findSavedProduct(grid, state.productPath);
    const savedTop = Number(state.productViewportTop);

    if (savedLink && Number.isFinite(savedTop)) {
      const card = productCard(grid, savedLink);
      const currentTop = card.getBoundingClientRect().top;
      return Math.max(
        0,
        Math.round(window.scrollY + currentTop - savedTop)
      );
    }

    return Math.max(0, Number(state.scrollY) || 0);
  };

  const restoreState = async (section, grid) => {
    const state = readSavedState(collectionKey());
    if (!state) {
      revealCollection();
      return;
    }

    const targetPage = Math.max(
      1,
      Math.floor(Number(state.pagesLoaded) || 1)
    );
    const alreadyLoaded = Math.max(
      1,
      Number(
        section.dataset.fmLastLoadedPage || parsePage(window.location.href)
      )
    );

    let previousScrollRestoration = null;
    if ('scrollRestoration' in history) {
      previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    try {
      for (let page = alreadyLoaded + 1; page <= targetPage; page += 1) {
        const { nextRoot, nextGrid } = await fetchCollectionPage(section, page);
        const fragment = document.createDocumentFragment();

        [...nextGrid.children].forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);
        section.dataset.fmLastLoadedPage = String(page);
        syncViewMore(section, nextRoot);
      }

      if (document.fonts?.ready) {
        await Promise.race([
          document.fonts.ready,
          new Promise((resolve) => setTimeout(resolve, 250))
        ]);
      }

      await nextFrame();
      await nextFrame();

      window.scrollTo({
        top: exactScrollTarget(grid, state),
        left: 0,
        behavior: 'auto'
      });

      await nextFrame();
    } catch (error) {
      console.warn('[FM collection restore]', error);

      window.scrollTo({
        top: Math.max(0, Number(state.scrollY) || 0),
        left: 0,
        behavior: 'auto'
      });
    } finally {
      revealCollection();

      if (previousScrollRestoration !== null) {
        history.scrollRestoration = previousScrollRestoration;
      }
    }
  };

  const initCollection = (section) => {
    if (
      !(section instanceof Element) ||
      section.dataset.fmBackRestoreReady === 'true'
    ) {
      return;
    }

    const grid = section.querySelector('[data-fm-product-grid]');
    if (!grid) return;

    section.dataset.fmBackRestoreReady = 'true';
    section.dataset.fmLastLoadedPage = String(parsePage(window.location.href));

    section.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const loadMore = target.closest('[data-fm-view-more]');
        if (loadMore && section.contains(loadMore)) {
          section.dataset.fmPendingPage = String(parsePage(loadMore.href));
          return;
        }

        const productLink = target.closest(
          '[data-fm-product-grid] a[href*="/products/"]'
        );
        if (!productLink || !section.contains(productLink)) return;

        rememberState(section, grid, productLink);
      },
      true
    );

    const observer = new MutationObserver((mutations) => {
      const addedCards = mutations.some(
        (mutation) =>
          mutation.type === 'childList' && mutation.addedNodes.length > 0
      );
      if (!addedCards) return;

      const pending = Number(section.dataset.fmPendingPage || 0);
      const current = Number(section.dataset.fmLastLoadedPage || 1);

      if (pending > current) {
        section.dataset.fmLastLoadedPage = String(pending);
      }
      delete section.dataset.fmPendingPage;
    });

    observer.observe(grid, { childList: true });
    restoreState(section, grid);
  };

  const init = (root = document) => {
    const sections = [];

    if (root instanceof Element && root.matches('.fm-collection')) {
      sections.push(root);
    }

    root.querySelectorAll?.('.fm-collection').forEach((section) => {
      sections.push(section);
    });

    [...new Set(sections)].forEach(initCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target || document);
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const section = document.querySelector('.fm-collection');
    const grid = section?.querySelector('[data-fm-product-grid]');
    if (section && grid) restoreState(section, grid);
  });
})();
